import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/browser'
import { API, ApiResponse } from './api'
import { LoggedIn } from '../models/user_model'
import { PasskeyApiErrorBody, PasskeyCredentialSummary } from '../models/passkey'

export type PasskeyErrorCode =
  | 'AUTH_PASSKEY_CHALLENGE_EXPIRED'
  | 'AUTH_PASSKEY_VERIFICATION_FAILED'
  | 'AUTH_PASSKEY_COUNTER_REPLAY'
  | 'AUTH_PASSKEY_CREDENTIAL_NOT_FOUND'
  | 'AUTH_PASSKEY_CREDENTIAL_NOT_OWNED'
  | 'AUTH_PASSKEY_USER_BLOCKED'
  | 'AUTH_PASSKEY_USER_AGENT_MISSING'
  // Client-side: the authenticator already has a passkey for this account
  | 'ALREADY_REGISTERED'
  | 'UNKNOWN'

const KNOWN_API_CODES: ReadonlySet<string> = new Set([
  'AUTH_PASSKEY_CHALLENGE_EXPIRED',
  'AUTH_PASSKEY_VERIFICATION_FAILED',
  'AUTH_PASSKEY_COUNTER_REPLAY',
  'AUTH_PASSKEY_CREDENTIAL_NOT_FOUND',
  'AUTH_PASSKEY_CREDENTIAL_NOT_OWNED',
  'AUTH_PASSKEY_USER_BLOCKED',
  'AUTH_PASSKEY_USER_AGENT_MISSING',
])

export type PasskeyResult<T> =
  | { status: 'success'; data: T }
  // The user dismissed the browser prompt — callers should do nothing
  | { status: 'cancelled' }
  | { status: 'error'; code: PasskeyErrorCode; message?: string }

export const isPasskeySupported = (): boolean =>
  typeof window !== 'undefined' && browserSupportsWebAuthn()

// @simplewebauthn's WebAuthnError preserves the original error's .name,
// so a plain name check covers both raw and wrapped errors.
const isUserCancellation = (e: unknown): boolean => {
  const name = (e as Error)?.name
  return name === 'NotAllowedError' || name === 'AbortError'
}

const mapApiError = <T>(
  res: ApiResponse<unknown, PasskeyApiErrorBody>
): PasskeyResult<T> => {
  const body = res.data as PasskeyApiErrorBody | undefined
  const code = body?.code
  return {
    status: 'error',
    code: code && KNOWN_API_CODES.has(code) ? (code as PasskeyErrorCode) : 'UNKNOWN',
    message: body?.message,
  }
}

export const registerPasskey = async (
  name?: string
): Promise<PasskeyResult<PasskeyCredentialSummary>> => {
  const api = API.getInstance()

  const optionsRes = await api.getPasskeyRegistrationOptions()
  if (!optionsRes.ok || !optionsRes.data) return mapApiError(optionsRes)

  let attestation: RegistrationResponseJSON
  try {
    attestation = await startRegistration({ optionsJSON: optionsRes.data })
  } catch (e) {
    if (isUserCancellation(e)) return { status: 'cancelled' }
    if ((e as Error)?.name === 'InvalidStateError') {
      return { status: 'error', code: 'ALREADY_REGISTERED' }
    }
    console.error('Passkey registration ceremony failed', e)
    return { status: 'error', code: 'UNKNOWN', message: (e as Error)?.message }
  }

  const verifyRes = await api.verifyPasskeyRegistration(attestation, name)
  if (!verifyRes.ok || !verifyRes.data) return mapApiError(verifyRes)
  return { status: 'success', data: verifyRes.data }
}

const runLoginCeremony = async (): Promise<PasskeyResult<LoggedIn>> => {
  const api = API.getInstance()

  const optionsRes = await api.getPasskeyLoginOptions()
  if (!optionsRes.ok || !optionsRes.data) return mapApiError(optionsRes)

  let assertion: AuthenticationResponseJSON
  try {
    assertion = await startAuthentication({ optionsJSON: optionsRes.data.options })
  } catch (e) {
    if (isUserCancellation(e)) return { status: 'cancelled' }
    console.error('Passkey login ceremony failed', e)
    return { status: 'error', code: 'UNKNOWN', message: (e as Error)?.message }
  }

  const verifyRes = await api.verifyPasskeyLogin(optionsRes.data.session_id, assertion)
  if (!verifyRes.ok || !verifyRes.data) return mapApiError(verifyRes)
  return { status: 'success', data: verifyRes.data }
}

/**
 * Usernameless passkey login. The server-side challenge lives for 5 minutes;
 * on expiry the whole ceremony is silently rerun exactly once (a second
 * expiry surfaces as an error).
 */
export const loginWithPasskey = async (): Promise<PasskeyResult<LoggedIn>> => {
  const first = await runLoginCeremony()
  if (first.status === 'error' && first.code === 'AUTH_PASSKEY_CHALLENGE_EXPIRED') {
    return runLoginCeremony()
  }
  return first
}
